<script setup lang="ts">
import { ArrowRight } from "lucide-vue-next";
import {
  getCategorySearchPath,
  getServiceSearchPath,
  toTitleCase,
} from "~/utils/search-catalog";
import type {
  SearchCategoryOption,
  SearchServiceOption,
} from "~/utils/search-catalog";

const props = defineProps<{
  categories: SearchCategoryOption[];
  services: SearchServiceOption[];
}>();

const localeRoute = useLocaleRoute();
const { t } = useI18n();

function sortCatalog<T extends { sortOrder?: number | null; name: string }>(
  items: T[],
) {
  return [...items].sort(
    (a, b) =>
      Number(a.sortOrder == null) - Number(b.sortOrder == null) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

function categoryLabel(category: string) {
  const key = `category.${category}`;
  const translated = t(key);

  return translated === key ? toTitleCase(category) : translated;
}

function serviceLabel(service: string) {
  const key = `services.${service}`;
  const translated = t(key);

  return translated === key ? toTitleCase(service) : translated;
}

const categories = computed(() =>
  sortCatalog(props.categories)
    .slice(0, 8)
    .map(category => ({
      title: categoryLabel(category.name),
      category: category.name,
      links: sortCatalog(
        props.services.filter(
          service => service.categoryName === category.name,
        ),
      )
        .slice(0, 3)
        .map(service => ({
          label: serviceLabel(service.name),
          service: service.name,
        })),
    })),
);

function getSearchRoute(category: string, service?: string) {
  return localeRoute(
    service
      ? getServiceSearchPath(category, service)
      : getCategorySearchPath(category),
  );
}
</script>

<template>
  <section class="w-full bg-[#F7F9F9] py-12 md:py-16">
    <div class="mx-auto w-full max-w-[1280px] px-4 md:px-6">
      <div class="mb-9 flex items-end justify-between gap-6 md:mb-10">
        <h2 class="font-gabarito text-2xl font-bold text-app-text md:text-3xl">
          {{ $t("services.title") }}
        </h2>

        <NuxtLink
          :to="localeRoute('/workers')"
          class="group hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition duration-300 hover:text-primary-400 sm:flex"
        >
          {{ $t("popular_services.actions.more") }}
          <ArrowRight
            class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </NuxtLink>
      </div>

      <div
        class="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-10 lg:gap-y-12"
      >
        <div v-for="item in categories" :key="item.category" class="min-w-0">
          <NuxtLink
            :to="getSearchRoute(item.category)"
            class="group inline-flex items-center gap-1.5 font-gabarito text-[17px] font-bold leading-6 text-app-text transition-colors duration-300 hover:text-primary"
          >
            {{ item.title }}
            <ArrowRight
              class="h-4 w-4 text-app-muted transition duration-300 group-hover:translate-x-0.5 group-hover:text-primary"
            />
          </NuxtLink>

          <ul class="mt-4 space-y-2.5">
            <li v-for="link in item.links" :key="link.service">
              <NuxtLink
                :to="getSearchRoute(item.category, link.service)"
                class="text-sm leading-5 text-app-muted transition-colors duration-300 hover:text-primary animated-underline"
              >
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>

          <NuxtLink
            :to="getSearchRoute(item.category)"
            class="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors duration-300 hover:text-primary-400"
          >
            {{ $t("popular_services.actions.more") }}
            <ArrowRight class="h-3.5 w-3.5" />
          </NuxtLink>
        </div>
      </div>

      <NuxtLink
        :to="localeRoute('/workers')"
        class="mt-9 inline-flex items-center gap-1.5 text-sm font-semibold text-primary sm:hidden"
      >
        {{ $t("popular_services.actions.more") }}
        <ArrowRight class="h-4 w-4" />
      </NuxtLink>
    </div>
  </section>
</template>
